import { Box, Flex, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';

export interface SettingsRowProps {
    label: string;
    description?: string;
    /** The input, switch, or select. Rendered right aligned on the same row. */
    control?: ReactNode;
    /** Render the control on its own line below the label instead of beside it. */
    isStacked?: boolean;
    /** Optional strip above the label/control, for a caveat that belongs to this row. */
    banner?: ReactNode;
    children?: ReactNode;
}

/**
 * One setting: label and description on the left, control on the right.
 * Dividers between rows come from the parent SettingsCard.
 */
const SettingsRow = ({
    label,
    description,
    control,
    isStacked = false,
    banner,
    children,
}: SettingsRowProps) => (
    <Box py={4}>
        {banner && <Box mb={4}>{banner}</Box>}
        <Flex
            align={isStacked ? 'flex-start' : 'center'}
            justify="space-between"
            gap={6}
            direction={isStacked ? 'column' : 'row'}
        >
            <Box flex={isStacked ? undefined : 1} minW={0}>
                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                    {label}
                </Text>
                {description && (
                    <Text fontSize="sm" color="gray.500" mt={0.5}>
                        {description}
                    </Text>
                )}
            </Box>
            {control && (
                <Box flexShrink={0} w={isStacked ? '100%' : undefined}>
                    {control}
                </Box>
            )}
        </Flex>
        {children && <Box mt={3}>{children}</Box>}
    </Box>
);

export interface SettingsValueProps {
    label: string;
    children: ReactNode;
}

/** Read-only label/value pair: small uppercase grey label above the value. */
export const SettingsValue = ({ label, children }: SettingsValueProps) => (
    <Box py={3}>
        <Text
            fontSize="11px"
            fontWeight="semibold"
            color="gray.500"
            letterSpacing="wider"
            textTransform="uppercase"
            mb={1}
        >
            {label}
        </Text>
        <Box fontSize="md" color="gray.800">
            {children}
        </Box>
    </Box>
);

export default SettingsRow;
